<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import { mdiCalendarPlus, mdiClose, mdiRestore } from "@mdi/js"
  import { reopenWorkOrder } from "../../../../../../../../../../api/kehrblatt/work-orders"
  import CreateAppointmentOverlay from "../../../../../../_shared/_modules/create-appointment-overlay"
  import { buildFixedWorkOrderAppointmentInit } from "../../../../../../_shared/_logic/init"
  import type { WorkingHoursByUser } from "../../../../../../../../../../_shared/_logic/form-state"
  import { internalOrderTypeLabel } from "../../../../../../../../../../_shared/_logic/order-type-labels"

  type Props = {
    organizationId: string
    workOrder: WorkOrder
    properties?: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    employees?: { id: string; label: string }[]
    scheduleEvents?: ScheduleEvent[]
    planningSettings?: PlanningSettings | null
    workingHoursByUser?: WorkingHoursByUser
    allEmployeeIds?: string[]
    onChanged?: (workOrder?: WorkOrder | null) => Promise<void> | void
    onremoveOverlay?: () => void
  }

  let {
    organizationId,
    workOrder,
    properties = [],
    customers = [],
    contactPoints = [],
    employees = [],
    scheduleEvents = [],
    planningSettings = null,
    workingHoursByUser = {},
    allEmployeeIds = [],
    onChanged = async () => {},
    onremoveOverlay = () => {},
  }: Props = $props()

  let submitting = $state(false)
  let reopenedWorkOrder = $state<WorkOrder | null>(null)

  const orderNumber = $derived(`${workOrder?.order_number ?? ""}`.trim())
  const orderType = $derived(internalOrderTypeLabel(workOrder) ?? "")
  const orderLabel = $derived(
    [orderType, orderNumber ? `#${orderNumber}` : null].filter(Boolean).join(" "),
  )

  const handleReopen = async () => {
    if (!workOrder?.id || submitting) return

    submitting = true
    try {
      const reopened = await reopenWorkOrder(workOrder.id)
      reopenedWorkOrder = reopened
      newNotification({ class: "success", html: "Auftrag wieder geöffnet." })
      await onChanged?.(reopened)
    } catch (err) {
      console.error("work_order_reopen_failed", err)
      const message =
        (err as { message?: string })?.message ?? "Auftrag konnte nicht wieder geöffnet werden."
      newNotification({ class: "error", html: message })
    } finally {
      submitting = false
    }
  }

  const openAppointment = () => {
    const targetWorkOrder = reopenedWorkOrder ?? workOrder

    if (!organizationId || !targetWorkOrder?.id) return

    openOverlay({
      title: "Termin anlegen",
      content: CreateAppointmentOverlay,
      bodyPadding: "none",
      closeOnParentOpen: true,
      isDark: true,
      properties: {
        init: buildFixedWorkOrderAppointmentInit({
          organizationId,
          employees,
          workOrder: targetWorkOrder,
          properties,
          customers,
          contactPoints,
          scheduleEvents,
          workingHoursByUser,
          defaultStart: null,
          defaultEnd: null,
          defaultFlexType: "HARD",
          defaultStatus: "RESERVED",
          flexSlotMinutes: planningSettings?.flex_slot_minutes ?? null,
          flexSlotsPerDay: planningSettings?.flex_slots_per_day ?? null,
          allEmployeeIds,
        }),
        onCreated: async () => {
          await onChanged?.(targetWorkOrder)
          onremoveOverlay?.()
        },
      },
    })
  }
</script>

<section class="reopen-overlay" aria-label="Auftrag wieder öffnen">
  <ActionBox label="Auftrag" darkMode={false} buttonSnippet={actionSnippet}>
    <div class="reopen-copy">
      <h2>{orderLabel || "Ausgewählter Auftrag"}</h2>
      {#if reopenedWorkOrder}
        <p>Der Auftrag ist wieder offen. Sie können jetzt direkt einen Termin dafür anlegen.</p>
      {:else}
        <p
          >Öffnen Sie den Auftrag wieder, wenn für diesen Auftrag erneut ein Termin geplant werden
          soll.</p
        >
      {/if}
    </div>
  </ActionBox>
</section>

{#snippet actionSnippet()}
  <div class="actions">
    <CTA type="secondaryButton" content="Schließen" onClick={onremoveOverlay}>
      {#snippet iconSnippet()}
        <Icon path={mdiClose} />
      {/snippet}
    </CTA>
    {#if reopenedWorkOrder}
      <CTA type="primaryButton" content="Termin anlegen" onClick={openAppointment}>
        {#snippet iconSnippet()}
          <Icon path={mdiCalendarPlus} />
        {/snippet}
      </CTA>
    {:else}
      <CTA
        type="primaryButton"
        content={submitting ? "Öffne…" : "Wieder öffnen"}
        onClick={handleReopen}
        disabled={submitting}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiRestore} />
        {/snippet}
      </CTA>
    {/if}
  </div>
{/snippet}

<style lang="less">
  .reopen-overlay {
    display: grid;
    min-width: min(34rem, 100%);
    color: var(--text-invers-100);
  }

  .reopen-copy {
    display: grid;
    gap: 0.55rem;
    padding-top: 0.1rem;

    h2 {
      margin: 0;
      font-size: 1.25rem;
      line-height: 1.2;
    }

    p {
      margin: 0;
      color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
      line-height: 1.45;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.7rem;
    flex-wrap: wrap;
  }
</style>
